import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {Conversion} from '../../domain/types';
import {convertCsvToXml, fetchConversions, fetchProjectConversions, deleteConversion} from './conversionsThunks';
import {apiErrorMessage} from '../../lib/apiError';

interface ConversionsState {
    conversions: Conversion[];
    projectConversions: Conversion[];
    loading: boolean;
    error: string | null;
    lastFetched: number | null; // Timestamp of last fetch for cache management
    isConverting: boolean; // Track if a conversion is in progress
    output: string; // Conversion output message
    transcriptionFiles: string[]; // List of available transcription files
    showConversions: boolean; // Show/hide conversions list
}

const initialState: ConversionsState = {
  conversions: [],
  projectConversions: [],
  loading: false,
  error: null,
  lastFetched: null,
  isConverting: false,
  output: '',
  transcriptionFiles: [],
  showConversions: false,
};

const conversionsSlice = createSlice({
  name: 'conversions',
  initialState,
  reducers: {
    setConversions: (state, action: PayloadAction<Conversion[]>) => {
      state.conversions = action.payload;
      state.error = null;
    },
    setProjectConversions: (state, action: PayloadAction<Conversion[]>) => {
      state.projectConversions = action.payload;
      state.error = null;
    },
    addConversion: (state, action: PayloadAction<Conversion>) => {
      state.conversions.push(action.payload);
    },
    removeConversion: (state, action: PayloadAction<string>) => {
      state.conversions = state.conversions.filter(c => c.id !== action.payload);
      state.projectConversions = state.projectConversions.filter(c => c.id !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    invalidateCache: (state) => {
            // Force next fetch to get fresh data
      state.lastFetched = null;
    },
    setIsConverting: (state, action: PayloadAction<boolean>) => {
      state.isConverting = action.payload;
    },
    setOutput: (state, action: PayloadAction<string>) => {
      state.output = action.payload;
    },
    setTranscriptionFiles: (state, action: PayloadAction<string[]>) => {
      state.transcriptionFiles = action.payload;
    },
    setShowConversions: (state, action: PayloadAction<boolean>) => {
      state.showConversions = action.payload;
    },
  },
  extraReducers: (builder) => {
        // Convert CSV to XML
    builder.addCase(convertCsvToXml.pending, (state) => {
      state.isConverting = true;
      state.error = null;
    });
    builder.addCase(convertCsvToXml.fulfilled, (state, action) => {
      state.isConverting = false;
      state.error = null;
            // Add the new conversion to both lists (it will be filtered by project later)
      const newConversion = action.payload.conversion;
      state.conversions.unshift(newConversion);
      if (newConversion.projectId) {
        state.projectConversions.unshift(newConversion);
      }
    });
    builder.addCase(convertCsvToXml.rejected, (state, action) => {
      state.isConverting = false;
      state.error = apiErrorMessage(action.payload, 'Could not convert this file');
    });

        // Fetch All Conversions
    builder.addCase(fetchConversions.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchConversions.fulfilled, (state, action) => {
      state.conversions = action.payload;
      state.loading = false;
      state.error = null;
      state.lastFetched = Date.now();
    });
    builder.addCase(fetchConversions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

        // Fetch Project Conversions
    builder.addCase(fetchProjectConversions.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchProjectConversions.fulfilled, (state, action) => {
      state.projectConversions = action.payload;
      state.loading = false;
      state.error = null;
      state.lastFetched = Date.now();
    });
    builder.addCase(fetchProjectConversions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

        // Delete Conversion
    builder.addCase(deleteConversion.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteConversion.fulfilled, (state, action) => {
      state.conversions = state.conversions.filter(c => c.id !== action.payload);
      state.projectConversions = state.projectConversions.filter(c => c.id !== action.payload);
      state.loading = false;
            // Invalidate cache after delete so next fetch gets fresh data
      state.lastFetched = null;
    });
    builder.addCase(deleteConversion.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const {
  setConversions,
  setProjectConversions,
  addConversion,
  removeConversion,
  setLoading,
  setError,
  invalidateCache,
  setIsConverting,
  setOutput,
  setTranscriptionFiles,
  setShowConversions,
} = conversionsSlice.actions;
export default conversionsSlice.reducer;
